import { Button, FormControlLabel, Popover, Stack, Switch, TextField, Typography } from '@mui/material';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { TPhoenixTable } from './../utility/types';

interface IShowColumnsPanelProps {
    table: TPhoenixTable;
    anchorEl: HTMLElement | null;
    tableContainerRef: React.RefObject<HTMLDivElement | null>;
    onClose: () => void;
}
export const ShowColumnsPanel = ({ table, anchorEl, tableContainerRef, onClose }: IShowColumnsPanelProps) => {
    const [searchValue, setSearchValue] = useState<string>('');
    const [tableRect, setTableRect] = useState<DOMRect | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const [open, setOpen] = useState(false);
    useLayoutEffect(() => {
        const updateTableRect = () => {
            if (tableContainerRef.current) {
                setTableRect(tableContainerRef.current.getBoundingClientRect());
            }
        };
        updateTableRect();
        window.addEventListener('resize', updateTableRect);
        window.addEventListener('scroll', updateTableRect, true);
        const resizeObserver = new ResizeObserver(updateTableRect);
        if (tableContainerRef.current) {
            resizeObserver.observe(tableContainerRef.current);
        }
        return () => {
            window.removeEventListener('resize', updateTableRect);
            window.removeEventListener('scroll', updateTableRect, true);
            resizeObserver.disconnect();
        };
    }, []);
    const columns = table.getAllLeafColumns().filter((column) => column.getCanHide());
    const filteredColumns = columns.filter((column) => {
        const columnName = String(column.columnDef.meta?.headerName ?? column.id).toLowerCase();
        return columnName.includes(searchValue.trim().toLowerCase());
    });
    const handleClose = () => {
        setSearchValue('');
        onClose();
        setOpen(false);
    };
    useEffect(() => {
        if (open) {
            requestAnimationFrame(() => {
                inputRef.current?.focus();
            });
        }
    }, [open]);
    const handlePopoverEntered = () => {
        inputRef.current?.focus();
    };
    const handleHideAll = () => {
        table.toggleAllColumnsVisible(false);
    };
    const handleShowAll = () => {
        table.toggleAllColumnsVisible(true);
    };
    return (
        <Popover
            open={!!anchorEl}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorReference="anchorPosition"
            anchorPosition={{
                top: (tableRect?.top ?? 0) + 40,
                left: tableRect?.left ?? 0,
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
            }}
            TransitionProps={{
                onEntered: handlePopoverEntered,
            }}
        // slotProps={{
        //     transition: {
        //         onEntered: () => {
        //             inputRef.current?.focus();
        //         },
        //     },
        // }}
        >
            <Stack
                spacing={2}
                sx={{ p: 1 }}
            >
                <TextField
                    size="small"
                    variant="standard"
                    label="Find Columns"
                    inputRef={inputRef}
                    placeholder="Column Titles"
                    value={searchValue}
                    onChange={(event) => setSearchValue(event.target.value)}
                    sx={{
                        minWidth: 280,
                        m: 1,
                        '& .MuiInputBase-input': {
                            fontWeight: 400,
                        },
                    }}
                />
                <Stack
                    spacing={1}
                    sx={{
                        minWidth: 300,
                        maxHeight: 400,
                        overflowY: 'auto',
                    }}
                >
                    {filteredColumns.length > 0 ? (
                        filteredColumns.map((column) => (
                            <FormControlLabel
                                key={column.id}
                                control={
                                    <Switch
                                        checked={column.getIsVisible()}

                                        disabled={!column.getCanHide()}
                                        onChange={column.getToggleVisibilityHandler()}
                                        size="small"
                                    />
                                }
                                label={column.columnDef.meta?.headerName ?? column.id}
                            />
                        ))
                    ) : (
                        <Typography
                            variant="body2"
                            sx={{
                                px: 1,
                                py: 2,
                                color: 'text.secondary',
                            }}
                        >
                            No data
                        </Typography>
                    )}
                </Stack>
                <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <Button
                        size="small"
                        onClick={handleHideAll}
                    >
                        Hide all
                    </Button>
                    <Button
                        size="small"
                        onClick={handleShowAll}

                    >
                        Show all
                    </Button>
                </Stack>
            </Stack>
        </Popover>
    );
};
